<template>
  <div v-if="waizmannwert.v1" class="dlg">
    <header class="dlg__head">
      <div class="dlg__eyebrow">
        <span class="dlg__dot"></span>
        WAIZMANNWERT&nbsp;/&nbsp;8-JAHRES REALWERT
      </div>
      <h2 class="dlg__title">Waizmann 8-Jahres Realwert-Methode</h2>
      <p class="dlg__sub">
        {{ tarif.un_name }} {{ tarif.tarif_title }} – Durchschnittserstattung
        nach 8 Jahren
      </p>
    </header>

    <div class="ww-grid">
      <div class="ww-kpi">
        <span class="ww-kpi__num">{{ tarif.tarif_waizmannwert }}<span class="ww-kpi__sign">%</span></span>
        <span class="ww-kpi__label">Ø-Erstattung</span>
        <span class="ww-kpi__sub">über 8 Jahre</span>
      </div>
      <div class="dlg__prose ww-prose">
        <p>
          Die <strong>Waizmann 8-Jahres Realwert-Methode</strong> gibt die reale
          Durchschnittserstattung einer Zahnzusatzversicherung über einen
          8-jährigen Beobachtungszeitraum an. Im Gegensatz zu üblichen
          Testverfahren ist die Berechnung für jeden
          <strong>nachprüfbar</strong> und
          <strong>100&nbsp;% manipulationssicher</strong>. Dies ist einer der
          Gründe, weshalb die Waizmanntabelle von immer mehr Zahnärzten in
          Deutschland empfohlen wird.
        </p>
        <p>
          Die <strong>{{ tarif.un_name }} {{ tarif.tarif_title }}</strong>
          leistet über 8 Jahre betrachtet im Durchschnitt
          <strong>{{ tarif.tarif_waizmannwert }}&nbsp;%</strong>. Basis für die
          Berechnung bildet der unten abgebildete Behandlungsverlauf.
        </p>
      </div>
    </div>

    <section class="dlg__section">
      <h3 class="dlg__section-title">Rechenweg | 8-Jahres Behandlungsverlauf</h3>
      <div class="dlg__table-wrap">
        <table class="dlg__table ww-table">
          <thead>
            <tr>
              <th class="ww-th-year">Jahr</th>
              <th>Leistung</th>
              <th class="dlg__th-num">Rechnung</th>
              <th class="dlg__th-num">GKV</th>
              <th class="dlg__th-num ww-th-zzv">ZZV-Leistung</th>
              <th class="dlg__th-num">Eigenanteil</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(row, idx) in treatments" :key="idx">
              <td class="ww-td-year">{{ row.year }}.</td>
              <td>{{ row.label }}</td>
              <td class="dlg__td-num">{{ formatEuro(row.rechnung) }}</td>
              <td class="dlg__td-num">{{ formatEuro(row.gkv) }}</td>
              <td class="dlg__td-num ww-td-zzv">
                {{ waizmannwert[row.key] }},- €
              </td>
              <td class="dlg__td-num">{{ waizmannwert[row.eaKey] }},- €</td>
            </tr>
            <tr class="ww-sum">
              <td colspan="2"><strong>Summe</strong></td>
              <td class="dlg__td-num"><strong>9.940,- €</strong></td>
              <td class="dlg__td-num"><strong>1.940,- €</strong></td>
              <td class="dlg__td-num ww-td-zzv">
                <strong>{{ waizmannwert.endgesamt }},- €</strong>
                <span class="ww-sum-pct">
                  = {{ tarif.tarif_waizmannwert }}&nbsp;%
                </span>
              </td>
              <td class="dlg__td-num">
                <strong>{{ waizmannwert.restgesamt }},- €</strong>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <div class="dlg__note dlg__note--info">
        <v-icon icon="mdi-information-outline" size="14" />
        Der Waizmannwert von <strong>{{ tarif.tarif_waizmannwert }}&nbsp;%</strong>
        ergibt sich aus der Summe der ZZV-Leistungen geteilt durch die
        Gesamt-Behandlungskosten von 9.940,-&nbsp;€.
      </div>
    </section>
  </div>
</template>

<script setup>
  import { defineProps, onMounted, ref } from "vue";
  import BaseCalls from "@/services/BaseCalls";

  const props = defineProps({
    tarif: { type: Object, default: () => ({}) },
    berechnung: { type: Object, default: () => ({}) },
    wwprop: { type: Object, default: () => ({ v1: false }) },
  });

  const waizmannwert = ref({});

  const treatments = [
    { year: 1, label: "Professionelle Zahnreinigung (2× jährlich)", rechnung: 160, gkv: 0, key: "v1", eaKey: "ea1" },
    { year: 1, label: "Dreiflächige Füllung in Adhäsivtechnik", rechnung: 200, gkv: 50, key: "v2", eaKey: "ea2" },
    { year: 2, label: "Professionelle Zahnreinigung (2× jährlich)", rechnung: 160, gkv: 0, key: "v3", eaKey: "ea3" },
    { year: 2, label: "Wurzelkanalbehandlung inkl. Einlage und Füllung", rechnung: 750, gkv: 0, key: "v4", eaKey: "ea4" },
    { year: 2, label: "Zwei dreiflächige Füllungen in Adhäsivtechnik", rechnung: 400, gkv: 100, key: "v5", eaKey: "ea5" },
    { year: 3, label: "Professionelle Zahnreinigung (2× jährlich)", rechnung: 160, gkv: 0, key: "v6", eaKey: "ea6" },
    { year: 3, label: "Inlay (mehrflächig) inkl. Labor", rechnung: 650, gkv: 50, key: "v7", eaKey: "ea7" },
    { year: 3, label: "Vollkrone in Stufenpräparation inkl. Keramikverblendung", rechnung: 800, gkv: 150, key: "v8", eaKey: "ea8" },
    { year: 4, label: "Professionelle Zahnreinigung (2× jährlich)", rechnung: 160, gkv: 0, key: "v9", eaKey: "ea9" },
    { year: 4, label: "Implantat inkl. Suprakonstruktion und chirurgischem Eingriff", rechnung: 2100, gkv: 520, key: "v10", eaKey: "ea10" },
    { year: 4, label: "Zwei dreiflächige Füllungen in Adhäsivtechnik", rechnung: 400, gkv: 100, key: "v11", eaKey: "ea11" },
    { year: 5, label: "Professionelle Zahnreinigung (2× jährlich)", rechnung: 160, gkv: 0, key: "b1", eaKey: "eb1" },
    { year: 5, label: "Dreiflächige Füllung in Adhäsivtechnik", rechnung: 200, gkv: 50, key: "b2", eaKey: "eb2" },
    { year: 6, label: "Professionelle Zahnreinigung (2× jährlich)", rechnung: 160, gkv: 0, key: "b3", eaKey: "eb3" },
    { year: 6, label: "Wurzelkanalbehandlung inkl. Einlage und Füllung", rechnung: 750, gkv: 0, key: "b4", eaKey: "eb4" },
    { year: 6, label: "Zwei dreiflächige Füllungen in Adhäsivtechnik", rechnung: 400, gkv: 100, key: "b5", eaKey: "eb5" },
    { year: 7, label: "Professionelle Zahnreinigung (2× jährlich)", rechnung: 160, gkv: 0, key: "b6", eaKey: "eb6" },
    { year: 7, label: "Inlay (mehrflächig) inkl. Labor", rechnung: 650, gkv: 50, key: "b7", eaKey: "eb7" },
    { year: 7, label: "Vollkrone in Stufenpräparation inkl. Keramikverblendung", rechnung: 800, gkv: 150, key: "b8", eaKey: "eb8" },
    { year: 8, label: "Professionelle Zahnreinigung (2× jährlich)", rechnung: 160, gkv: 0, key: "b9", eaKey: "eb9" },
    { year: 8, label: "Implantat inkl. Suprakonstruktion und chirurgischem Eingriff", rechnung: 2100, gkv: 520, key: "b10", eaKey: "eb10" },
    { year: 8, label: "Zwei dreiflächige Füllungen in Adhäsivtechnik", rechnung: 400, gkv: 100, key: "b11", eaKey: "eb11" },
  ];

  function formatEuro(num) {
    return new Intl.NumberFormat("de-DE").format(num) + ",- €";
  }

  async function getWaizmannwert() {
    let berSelect = {};
    berSelect.action = "getTarifWaizmannwert";
    berSelect.ww_wtb_id = props.tarif.tarif_id;
    try {
      const resp = await BaseCalls.postTarif(berSelect);
      if (resp.data && resp.data.waizmannwert) {
        waizmannwert.value = resp.data.waizmannwert;
      }
    } catch (err) {
      console.log("error berechnung");
    }
  }

  onMounted(() => {
    if (!props.wwprop.value) {
      getWaizmannwert();
    } else {
      waizmannwert.value = props.wwprop.value;
    }
  });
</script>

<style scoped>
  .ww-grid {
    display: grid;
    grid-template-columns: 140px minmax(0, 1fr);
    gap: 16px;
    align-items: start;
    margin-bottom: 16px;
  }
  @media (max-width: 720px) {
    .ww-grid {
      grid-template-columns: 1fr;
    }
  }

  .ww-kpi {
    background: #0f4c81;
    color: #fff;
    border-radius: 3px;
    padding: 14px 12px;
    text-align: center;
    display: flex;
    flex-direction: column;
    justify-content: center;
    min-height: 132px;
  }
  .ww-kpi__num {
    font-size: 36px;
    font-weight: 800;
    line-height: 1;
    letter-spacing: -0.5px;
    font-variant-numeric: tabular-nums;
  }
  .ww-kpi__sign {
    font-size: 22px;
    font-weight: 700;
    margin-left: 1px;
  }
  .ww-kpi__label {
    margin-top: 6px;
    font-size: 12px;
    font-weight: 700;
    letter-spacing: 0.4px;
    text-transform: uppercase;
    opacity: 0.95;
  }
  .ww-kpi__sub {
    margin-top: 2px;
    font-size: 10.5px;
    opacity: 0.8;
    letter-spacing: 0.3px;
    text-transform: uppercase;
  }

  .ww-prose {
    font-size: 13.5px;
  }

  .ww-table .ww-th-year,
  .ww-table .ww-td-year {
    width: 38px;
    text-align: center;
    color: #5b6473;
    font-weight: 700;
    font-variant-numeric: tabular-nums;
  }
  .ww-table .ww-th-zzv {
    color: #2e7d32;
  }
  .ww-table .ww-td-zzv {
    color: #2e7d32;
    font-weight: 700;
  }
  .ww-table .ww-sum {
    background: #f4f8fc;
  }
  .ww-table .ww-sum:hover {
    background: #f4f8fc;
  }
  .ww-table .ww-sum td {
    border-top: 2px solid #0f4c81;
    border-bottom: none;
    font-size: 13px;
  }
  .ww-sum-pct {
    display: block;
    font-size: 11px;
    font-weight: 700;
    color: #0f4c81;
    letter-spacing: 0.3px;
  }
</style>
